import React, {
    useState,
    useRef,
    useEffect,
    RefObject,
    ChangeEvent,
    KeyboardEvent,
    MouseEvent,
    FocusEvent,
    ReactElement,
} from 'react';

import { stopPropagation } from '../utils/stopPropagation';
import { hasValue, getDefaultValue } from '../utils';

import {
    SelectStyle,
    SelectInputStyle,
    SelectMenuStyle,
    SelectMenuItemStyle,
    SelectInputWrapper,
    SelectIcon,
} from '../styles/form/select.styles';

export interface OptionType {
    value: string | number;
    label: string;
    [key: string]: any;
}

export interface SelectProps {
    component?: React.ElementType;
    inputComponent?: React.ElementType;
    menuComponent?: React.ElementType;
    menuItemComponent?: React.ElementType;
    options?: OptionType[];
    optionRenderer?: (option: OptionType, index: number, isSelected: boolean, isHighlighted: boolean) => ReactElement;
    onChange?: (option: OptionType) => void;
    placeholder?: string;
    searchable?: boolean;
    name?: string;
    closeOnSelect?: boolean;
    value?: OptionType | string | number;
    defaultValue?: OptionType;
    innerRef?: RefObject<HTMLInputElement>;
    autoFocus?: boolean;
    showIcon?: boolean;
    noPlaceholder?: boolean;
    [key: string]: any;
}

export const Select: React.FC<SelectProps> & { Style?: any } = ({
    component: Component,
    inputComponent: InputComponent,
    menuComponent: MenuComponent,
    menuItemComponent: MenuItemComponent,
    options = [],
    optionRenderer,
    onChange,
    placeholder,
    searchable,
    name,
    closeOnSelect = true,
    value: propsValue,
    defaultValue,
    innerRef,
    autoFocus,
    showIcon = true,
    noPlaceholder = false,
    ...rest
}) => {
    const [value, setValue] = useState<OptionType | undefined>(getDefaultValue(defaultValue, options));
    const [currentScroll, setCurrentScroll] = useState<number>(
        value ? options.findIndex((option) => option.value === value.value) : 0,
    );
    const [search, setSearch] = useState<string>('');
    const [filteredOptions, setOptions] = useState<OptionType[]>(options);
    const [showMenu, shouldShowMenu] = useState<boolean>(false);
    const thisRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const optionRefs = useRef<(HTMLDivElement | null)[]>([]);

    const SelectComponent = Component || SelectStyle;
    const SelectInputComponent = InputComponent || SelectInputStyle;
    const SelectMenuComponent = MenuComponent || SelectMenuStyle;
    const SelectMenuItemComponent = MenuItemComponent || SelectMenuItemStyle;

    const defaultRenderer = (option: OptionType) => <SelectMenuItemComponent>{option.label}</SelectMenuItemComponent>;

    const handleReset = React.useCallback(
        (closeMenu?: boolean, selectedValue?: OptionType) => {
            if (selectedValue) setValue(selectedValue);
            if (closeMenu) shouldShowMenu(false);
            const index = options.findIndex((option) => {
                if (selectedValue) {
                    return option.value === selectedValue.value;
                } else if (value) {
                    return option.value === value.value;
                } else if (defaultValue) {
                    return option.value === defaultValue.value;
                }
                return false;
            });
            setCurrentScroll(index !== -1 ? index : 0);
            setSearch('');
            optionRefs.current = [];
            if (selectedValue && onChange) onChange(selectedValue);
        },
        [options, value, defaultValue, onChange],
    );

    const handleChange = (selectedValue: OptionType) => (e: MouseEvent | KeyboardEvent) => {
        if ('persist' in e) e.persist();
        stopPropagation(e);
        handleReset(closeOnSelect, selectedValue);
    };

    const handleFocus = (e: FocusEvent | MouseEvent) => {
        if ('persist' in e) e.persist();
        stopPropagation(e);
        shouldShowMenu(true);
        if (inputRef && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleRootEscape = React.useCallback(
        (e: KeyboardEvent) => {
            if (showMenu && e.key === 'Escape') {
                handleReset(true);
            }
        },
        [showMenu, handleReset],
    );

    const handleRootClose = React.useCallback(
        (e: MouseEvent) => {
            if (showMenu && thisRef.current && !thisRef.current.contains(e.target as Node)) {
                handleReset(true);
            }
        },
        [showMenu, handleReset, thisRef],
    );

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        e.persist();
        stopPropagation(e);
        if (showMenu) {
            const arrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' ? e.key : false;
            if (filteredOptions) {
                let scrollValue = currentScroll;
                if (arrow) {
                    if (arrow === 'ArrowUp' && currentScroll > 0) {
                        scrollValue--;
                    }
                    if (arrow === 'ArrowDown' && currentScroll < filteredOptions.length - 1) {
                        scrollValue++;
                    }
                    setCurrentScroll(scrollValue);
                    if (optionRefs.current[scrollValue]) {
                        optionRefs.current[scrollValue]!.scrollIntoView({ block: 'nearest' });
                    }
                }
                if (e.key === 'Enter') {
                    handleChange(filteredOptions[scrollValue])(e);
                }
                return true;
            }
        }
        return false;
    };

    const handleMouseOver = (index: number) => () => {
        setCurrentScroll(index);
    };

    const handleRef = (ref: HTMLDivElement | null) => {
        optionRefs.current.push(ref);
    };

    const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value);

    useEffect(() => {
        document.addEventListener('click', handleRootClose as any, true);
        document.addEventListener('keyup', handleRootEscape as any, true);
        return () => {
            document.removeEventListener('click', handleRootClose as any, true);
            document.removeEventListener('keyup', handleRootEscape as any, true);
        };
    }, [showMenu, handleRootClose, handleRootEscape]);

    useEffect(() => {
        if (search) {
            const lowerCaseSearch = search.replace(/\s/g, '').toLowerCase();
            setOptions(
                options.filter(
                    ({ value, label }) =>
                        value.toString().toLowerCase().replace(/\s/g, '').includes(lowerCaseSearch) ||
                        label.toString().toLowerCase().replace(/\s/g, '').includes(lowerCaseSearch),
                ),
            );
            setCurrentScroll(0);
        } else {
            setOptions(options);
        }
    }, [search, options]);

    useEffect(() => {
        if (JSON.stringify(filteredOptions) !== JSON.stringify(options)) {
            setOptions(options);
            handleReset(false);
        }
    }, [filteredOptions, setOptions, options, handleReset]);

    const renderer = optionRenderer || defaultRenderer;

    const getAppropriateValue = () => {
        const inputValues = {
            value: '',
            label: '',
        };
        if (propsValue && hasValue(propsValue)) {
            if (typeof propsValue === 'object') {
                inputValues.label = String((propsValue as OptionType).label);
                inputValues.value = String((propsValue as OptionType).value);
            } else {
                const currentValue = filteredOptions.find((opt) => opt.value === propsValue);
                inputValues.label = currentValue ? String(currentValue.label) : '';
                inputValues.value = currentValue ? String(currentValue.value) : '';
            }
        } else if (value && hasValue(value.value)) {
            inputValues.label = String(value.label);
            inputValues.value = String(value.value);
        } else if (defaultValue && hasValue(defaultValue.value)) {
            inputValues.label = String(defaultValue.label);
            inputValues.value = String(defaultValue.value);
        }
        if (showMenu && searchable && hasValue(search)) {
            inputValues.label = search;
        }
        return inputValues;
    };

    const { value: trueValue, label: trueLabel } = getAppropriateValue();

    return (
        <SelectComponent {...rest} className="select" innerRef={thisRef} onSelect={handleFocus}>
            <SelectInputWrapper>
                <SelectInputComponent
                    type="text"
                    readOnly={!searchable}
                    placeholder={noPlaceholder ? '' : placeholder || 'Select something..'}
                    ref={inputRef}
                    value={trueLabel}
                    menuIsOpen={showMenu}
                    onKeyDown={handleKeyDown}
                    onKeyUp={stopPropagation}
                    onKeyPress={stopPropagation}
                    onChange={handleSearch}
                    onFocus={handleFocus}
                    autoFocus={autoFocus}
                    tabIndex={0}
                />
                {showIcon && <SelectIcon invert={showMenu}>⌵</SelectIcon>}
            </SelectInputWrapper>
            <input type="hidden" value={trueValue} name={name} ref={innerRef} />
            {showMenu && options && (
                <SelectMenuComponent>
                    {filteredOptions.map((option, index) => {
                        const isSelected = trueValue === option.value;
                        const isHighlighted = currentScroll === index;
                        return React.cloneElement(renderer(option, index, isSelected, isHighlighted), {
                            key: `enkel-select-option-${index}`,
                            onClick: handleChange(option),
                            onMouseOver: handleMouseOver(index),
                            ref: handleRef,
                            option,
                            index,
                            isSelected,
                            isHighlighted,
                        });
                    })}
                </SelectMenuComponent>
            )}
        </SelectComponent>
    );
};

Select.Style = {
    Main: SelectStyle,
    Input: SelectInputStyle,
    InputWrapper: SelectInputWrapper,
    Menu: SelectMenuStyle,
    MenuItem: SelectMenuItemStyle,
};
